
import React, { useState, useMemo, useEffect } from 'react';
import { ForgivenessProgram, ProgramStatus, ClientProfile, SubmissionLog, UserData } from '../types';
import { SparkleIcon } from './icons/SparkleIcon';
import { TrendingUpIcon } from './icons/TrendingUpIcon';
import { ApplicationsIcon } from './icons/ApplicationsIcon';
import { ClockIcon } from './icons/ClockIcon';
import { ArrowRightIcon } from './icons/ArrowRightIcon';
import ApplicationProcess, { programRequirements, allDocsConfig } from './ApplicationProcess';
import { XIcon } from './icons/XIcon';
import PdfPreviewModal from './PdfPreviewModal';
import { DocumentTextIcon } from './icons/DocumentTextIcon';
import { CheckCircleIcon } from './icons/CheckCircleIcon';
import { ShieldIcon } from './icons/ShieldIcon';
import { InformationCircleIcon } from './icons/InformationCircleIcon';
import SubmissionConfirmationModal from './SubmissionConfirmationModal';
import InfoModal from './InfoModal';
import { CheckEligibilityIcon } from './icons/CheckEligibilityIcon';
import { ChevronDownIcon } from './icons/ChevronDownIcon';
import { PlusIcon } from './icons/PlusIcon';
import { TrophyIcon } from './icons/TrophyIcon';
import { DownloadIcon } from './icons/DownloadIcon';
import { TermsAndConditionsContent, PrivacyPolicyContent } from './LegalContent';
import RefundRequestModal from './RefundRequestModal';
import UnifiedAIAssistant from './UnifiedAIAssistant';

// --- Props ---
interface ResultsDashboardProps {
  clientProfile: ClientProfile;
  allClients: Record<string, ClientProfile>;
  onSwitchClient: (clientId: string) => void;
  onAddNewClient: () => void;
  onUpgrade: () => void;
  onApplicationSubmit: (programIds: string[], data: Record<string, any>, documents: Record<string, File>) => void;
  isTestMode: boolean;
  onToggleTestMode: () => void;
  onUpdateProgramStatus: (programId: string, status: ProgramStatus) => void;
  tempFiles: Record<string, Record<string, File>>;
  onRecheckEligibility: () => void;
  onDeleteAccount: () => void;
  multiClientModeEnabled: boolean;
  onToggleMultiClientMode: () => void;
  submissionModalId: string | null;
  onConfirmationModalClose: () => void;
}

// --- Sub-components ---

const ProgramStatusStepper: React.FC<{ status: ProgramStatus }> = ({ status }) => {
    let activeIndex = 0;
    if (status === 'started') activeIndex = 1;
    else if (status === 'submitted' || status === 'in-review' || status === 'approved') activeIndex = 2;
    return (
        <div className="w-full mt-6 mb-2">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex relative">
                <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${((activeIndex + 1) / 3) * 100}%` }}></div>
            </div>
            <div className="flex justify-between items-center mt-2 text-xs font-bold text-gray-500 uppercase tracking-wide">
                <span className="text-green-600">Eligible</span>
                <span className={activeIndex >= 1 ? "text-green-600" : ""}>Preparation</span>
                <span className={activeIndex >= 2 ? "text-green-600" : ""}>Service Complete</span>
            </div>
        </div>
    );
};

const ProgramCard: React.FC<{ 
    program: ForgivenessProgram; 
    onUpdateStatus: (status: ProgramStatus) => void; 
    onDownloadPacket: () => void;
}> = ({ program, onUpdateStatus, onDownloadPacket }) => {
    const isManual = program.submissionType === 'manual';
    const isComplete = program.status === 'submitted' || program.status === 'in-review' || program.status === 'approved';

    return (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm mb-6">
            <div className="flex justify-between items-start mb-4">
                <span className="bg-yellow-100 text-yellow-800 text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider">
                    {isComplete ? 'Processing' : 'Preparation Needed'}
                </span>
                <div className="relative group">
                    <button className="text-xs font-semibold text-gray-600 border border-gray-300 rounded-lg px-3 py-1.5 flex items-center gap-1 hover:bg-gray-50">
                        Update Status <ChevronDownIcon className="w-3 h-3" />
                    </button>
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg hidden group-hover:block z-10 p-1">
                        <button onClick={() => onUpdateStatus('in-review')} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 rounded">In Review</button>
                        <button onClick={() => onUpdateStatus('approved')} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 rounded text-green-600">Approved</button>
                    </div>
                </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">{program.name}</h3>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">{program.description}</p>
            <div className={`flex flex-col sm:flex-row items-start gap-4 p-5 mb-6 rounded-xl border ${isManual ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className={`p-2 rounded-full flex-shrink-0 bg-white shadow-sm ${isManual ? 'text-red-600' : 'text-green-600'}`}>
                    {isManual ? <DocumentTextIcon className="w-6 h-6" /> : <CheckCircleIcon className="w-6 h-6" />}
                </div>
                <div>
                    <h4 className={`font-bold text-sm uppercase tracking-wide mb-1 ${isManual ? 'text-red-900' : 'text-green-900'}`}>{isManual ? 'Manual Submission Required' : 'Automatic Submission'}</h4>
                    <p className={`text-xs leading-relaxed ${isManual ? 'text-red-800' : 'text-green-800'}`}>{isManual ? "Packet ready for mail-in." : "Submitted electronically."}</p>
                    {isManual && isComplete && (
                        <button onClick={onDownloadPacket} className="mt-4 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 text-xs">
                            <DownloadIcon className="w-4 h-4" /> Download Packet
                        </button>
                    )}
                </div>
            </div>
            <ProgramStatusStepper status={program.status} />
        </div>
    );
};

const UnlockSidebarWidget: React.FC<{ 
    programs: ForgivenessProgram[]; 
    onUpgrade: () => void; 
    onShowTerms: () => void; 
    onShowPrivacy: () => void; 
}> = ({ programs, onUpgrade, onShowTerms, onShowPrivacy }) => {
    const [agreed, setAgreed] = useState(false);
    const requiredDocs = useMemo(() => {
        const docSet = new Set<string>();
        programs.forEach(p => programRequirements[p.id]?.docs.forEach(docName => docSet.add(docName)));
        return Array.from(docSet).map(name => allDocsConfig[name]);
    }, [programs]);
    return (
        <div className="bg-white border-2 border-blue-500 rounded-2xl p-6 shadow-md">
            <div className="flex items-center gap-2 mb-4"><SparkleIcon className="w-5 h-5 text-blue-600" /><h3 className="text-lg font-bold text-gray-900">Unlock Full Support</h3></div>
            {requiredDocs.length > 0 && <div className="space-y-2 mb-4">{requiredDocs.map(doc => <div key={doc.name} className="flex gap-2 bg-gray-50 p-2 rounded text-xs border border-gray-200"><DocumentTextIcon className="w-4 h-4 text-gray-400 mt-0.5" /><div><span className="font-bold block">{doc.name}</span><span className="text-[10px] text-gray-500">{doc.description}</span></div></div>)}</div>}
            <label className="flex items-start gap-2 text-xs text-gray-600 mb-4 cursor-pointer"><input type="checkbox" checked={agreed} onChange={e=>setAgreed(e.target.checked)} className="mt-0.5 rounded"/><span className="leading-tight">I agree to the <button onClick={onShowTerms} className="underline text-blue-600">Terms</button> and <button onClick={onShowPrivacy} className="underline text-blue-600">Privacy Policy</button>.</span></label>
            <button onClick={onUpgrade} disabled={!agreed} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-black py-3 px-4 rounded-xl shadow-lg transition-transform transform hover:scale-105 uppercase tracking-widest text-xs">Upgrade & Start Application</button>
        </div>
    );
};

const ResultsDashboard: React.FC<ResultsDashboardProps> = ({ 
    clientProfile, 
    onUpgrade, 
    onApplicationSubmit, 
    isTestMode, 
    onUpdateProgramStatus,
    onRecheckEligibility,
    onDeleteAccount,
    submissionModalId,
    onConfirmationModalClose
}) => {
    const [viewMode, setViewMode] = useState<'dashboard' | 'settings'>('dashboard');
    const [showTerms, setShowTerms] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const isPaid = clientProfile.paymentStatus === 'paid';
    const programs = clientProfile.eligiblePrograms;
    const userData = clientProfile.userData;
    const pendingPrograms = programs.filter(p => p.status === 'action-required' || p.status === 'started');

    return (
        <div className="space-y-8 max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div><h1 className="text-4xl font-black text-gray-900 tracking-tighter flex items-center gap-2 uppercase">{userData.name}<ChevronDownIcon className="w-6 h-6 text-gray-300" /></h1><p className="text-gray-500 font-bold uppercase text-[10px] tracking-widest mt-1">ForgivePath Command Center</p></div>
                <div className="bg-gray-100 p-1 rounded-xl flex items-center font-black text-[10px] uppercase tracking-widest">
                    <button onClick={() => setViewMode('dashboard')} className={`px-5 py-2.5 rounded-lg transition-all ${viewMode === 'dashboard' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Dashboard</button>
                    <button onClick={() => setViewMode('settings')} className={`px-5 py-2.5 rounded-lg transition-all ${viewMode === 'settings' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Account</button>
                </div>
            </div>

            {viewMode === 'dashboard' && (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-8">
                            {!isPaid && <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-3xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6"><div className="text-left"><span className="bg-white/20 px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest border border-white/30 mb-2 inline-block">Premium Goal</span><h2 className="text-3xl font-black mb-2 uppercase tracking-tighter">Your Financial Freedom Plan</h2><p className="text-green-50 text-sm">We've identified {programs.length} programs. Upgrade to finalize everything.</p></div><button onClick={onUpgrade} className="bg-white text-green-700 font-black py-4 px-10 rounded-2xl shadow-lg transition-transform transform hover:scale-105 uppercase tracking-widest text-xs">Unlock Now</button></div>}
                            
                            {isPaid && pendingPrograms.length > 0 && (
                                <div id="application-process-section" className="bg-white border border-gray-200 rounded-3xl p-8 shadow-sm">
                                    <h3 className="text-xl font-black text-gray-900 mb-6 uppercase tracking-widest">Action Needed: Document Verification</h3>
                                    <ApplicationProcess userData={userData} programs={pendingPrograms} onComplete={() => {}} onSubmissionComplete={onApplicationSubmit} isTestMode={isTestMode} userId={clientProfile.ownerId} clientId={clientProfile.clientId} existingDraft={clientProfile.draft}/>
                                </div>
                            )}

                            <div className="space-y-6">
                                <div className="flex justify-between items-center"><h2 className="text-xl font-black text-gray-900 uppercase tracking-widest">Eligibility Status</h2><button onClick={onRecheckEligibility} className="text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-blue-600 flex items-center gap-2 border border-gray-200 px-3 py-1.5 rounded-lg bg-white">Re-Scan Eligibility</button></div>
                                {programs.map(program => <ProgramCard key={program.id} program={program} onUpdateStatus={(s) => onUpdateProgramStatus(program.id, s)} onDownloadPacket={() => {}}/>)}
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4"><div className="p-3 bg-green-50 rounded-xl text-green-600"><TrendingUpIcon className="w-6 h-6"/></div><div><p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Est. Savings</p><p className="text-2xl font-black text-gray-900 tracking-tighter">$10,000+</p></div></div>
                            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4"><div className="p-3 bg-blue-50 rounded-xl text-blue-600"><DocumentTextIcon className="w-6 h-6"/></div><div><p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Eligible Programs</p><p className="text-2xl font-black text-gray-900 tracking-tighter">{programs.length}</p></div></div>
                            {!isPaid && <UnlockSidebarWidget programs={programs} onUpgrade={onUpgrade} onShowTerms={()=>setShowTerms(true)} onShowPrivacy={()=>setShowPrivacy(true)}/>}
                        </div>
                    </div>
                    {/* PERSISTENT SUPPORT HUB AT THE BOTTOM OF THE DASHBOARD */}
                    <div className="mt-12">
                        <UnifiedAIAssistant 
                            variant="inline"
                            onSubmitQuiz={()=>{}}
                            userEmail={userData.email}
                            userId={clientProfile.ownerId}
                            clientId={clientProfile.clientId}
                            paymentStatus={clientProfile.paymentStatus}
                            hasSubmitted={clientProfile.submissionLogs.length > 0}
                        />
                    </div>
                </>
            )}

            {viewMode === 'settings' && <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-3xl p-8 shadow-sm"><h2 className="text-2xl font-black text-gray-900 uppercase tracking-tighter mb-8">Account Management</h2><div className="space-y-10"><section><h3 className="text-sm font-black text-gray-400 uppercase tracking-widest mb-4">Profile</h3><div className="space-y-2 text-sm font-bold text-gray-700"><p>Name: {userData.name}</p><p>Email: {userData.email}</p></div></section><hr/><section><button onClick={onDeleteAccount} className="text-sm font-black text-red-600 hover:underline uppercase tracking-widest">Delete Account Data</button></section></div></div>}
            {showTerms && <InfoModal title="Terms" onClose={()=>setShowTerms(false)}><TermsAndConditionsContent/></InfoModal>}
            {showPrivacy && <InfoModal title="Privacy" onClose={()=>setShowPrivacy(false)}><PrivacyPolicyContent/></InfoModal>}
        </div>
    );
};
export default ResultsDashboard;
